/** Admin editor: the bank account installers are told to pay into, per business. */
import React, { useEffect, useState } from 'react';
import { Landmark, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { loadBankDetails, resetBankDetailsCache } from '@/components/orders/BankDetails';

const FIELDS = [['account_name', 'Account name'], ['account_number', 'Account number'], ['sort_code', 'Sort code'], ['note', 'Note (optional)']];

function Row({ b, value, canEdit }) {
  const [v, setV] = useState(value || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => setV(value || {}), [value]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/bank-details/${b.key}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(v) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Save failed');
      resetBankDetailsCache(); toast.success(`${b.label} bank details saved`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="grid sm:grid-cols-[120px_1fr_auto] gap-2 items-start py-2 border-t first:border-t-0" data-testid={`bank-row-${b.key}`}>
      <div><div className="font-bold text-sm">{b.label}</div><div className="text-[11px] text-gray-500">{b.tenant_name || b.desc}</div></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {FIELDS.map(([k, l]) => <Input key={k} value={v[k] || ''} onChange={(e) => setV((o) => ({ ...o, [k]: e.target.value }))} placeholder={l} disabled={!canEdit} className="h-8 text-xs bg-white" data-testid={`bank-${b.key}-${k}`} />)}
      </div>
      {canEdit && <Button size="sm" onClick={save} disabled={busy} className="h-8 bg-[#1f2a37] text-white" data-testid={`bank-save-${b.key}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}</Button>}
    </div>
  );
}

export function BankDetailsSettings({ businesses, canEdit }) {
  const [all, setAll] = useState(null);
  useEffect(() => { loadBankDetails().then(setAll).catch(() => setAll({})); }, []);
  if (!all) return null;
  return (
    <Card className="p-3" data-testid="bank-details-settings">
      <div className="flex items-center gap-2 mb-1"><Landmark className="w-4 h-4 text-emerald-700" /><h2 className="text-sm font-bold">Bank details per business</h2></div>
      <p className="text-[11px] text-gray-500 mb-2">Shown to installers when they pay by bank transfer and in the "amount confirmed" email. Bank details printed on Xero invoices are set inside each Xero organisation (Invoice settings → branding theme).</p>
      {businesses.map((b) => <Row key={b.key} b={b} value={all[b.key]} canEdit={canEdit} />)}
    </Card>
  );
}
